import { ChevronLeft, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Dialog } from "../../components/ui/Dialog";
import { EmptyState, Skeleton } from "../../components/ui/Feedback";
import { Input, Select } from "../../components/ui/Input";
import { useProduct, useProducts } from "../products/useProducts";
import { formatMoney, titleCase } from "../../lib/format";
import type { Product } from "../../lib/types";

export interface PickedProduct {
	productId: string;
	variantId: string | null;
	selectVariant: boolean;
	/** Shopify ids, for callers that store what the discount function matches on. */
	productGid: string;
	variantGid: string | null;
}

interface ProductPickerProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onPick: (picked: PickedProduct) => void;
	title?: string;
	busy?: boolean;
	/**
	 * Only offer products sold on a selling plan. A subscription offer cannot be
	 * pitched on anything else, so picking one is a mistake worth preventing
	 * rather than explaining after the fact.
	 */
	subscribableOnly?: boolean;
	/** Replaces the variant step's description, for callers where quantity is not set after picking. */
	variantHint?: string;
}

const SEARCH_DEBOUNCE_MS = 250;

const ProductRow = ({ product, onSelect }: { product: Product; onSelect: () => void }) => (
	<button
		type="button"
		onClick={onSelect}
		className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-raised"
	>
		{product.thumbnailUrl ? (
			<img
				src={product.thumbnailUrl}
				alt=""
				loading="lazy"
				className="h-9 w-9 shrink-0 rounded-md border border-line object-cover"
			/>
		) : (
			<div className="h-9 w-9 shrink-0 rounded-md border border-dashed border-line" />
		)}
		<span className="min-w-0 flex-1">
			<span className="block truncate text-sm text-ink">{product.title ?? "Untitled"}</span>
			<span className="block truncate text-xs text-ink-faint">{product.handle ?? "—"}</span>
		</span>
		<Badge variant={product.status === "ACTIVE" ? "solid" : "outline"}>
			{titleCase(product.status)}
		</Badge>
	</button>
);

export const ProductPicker = ({
	open,
	onOpenChange,
	onPick,
	title = "Choose a product",
	busy = false,
	subscribableOnly = false,
	variantHint = "Pick one variant to auto-add, or let the customer choose. Set how many they get after adding.",
}: ProductPickerProps) => {
	const [searchInput, setSearchInput] = useState("");
	const [search, setSearch] = useState("");
	const [status, setStatus] = useState("");
	const [selected, setSelected] = useState<Product | null>(null);

	useEffect(() => {
		const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [searchInput]);

	useEffect(() => {
		if (!open) {
			setSelected(null);
			setSearchInput("");
			setSearch("");
			setStatus("");
		}
	}, [open]);

	const list = useProducts({
		search,
		status,
		limit: 25,
		offset: 0,
		subscribable: subscribableOnly,
	});
	const detail = useProduct(selected?.id);

	const pickVariant = (variantId: string | null, selectVariant: boolean) => {
		if (!selected) return;
		const variant = detail.data?.variants.find((entry) => entry.id === variantId);
		onPick({
			productId: selected.id,
			variantId,
			selectVariant,
			productGid: selected.shopifyGid,
			variantGid: variant?.shopifyGid ?? null,
		});
	};

	return (
		<Dialog
			open={open}
			onOpenChange={onOpenChange}
			title={selected ? (selected.title ?? "Choose a variant") : title}
			description={
				selected
					? variantHint
					: subscribableOnly
						? "Only products sold on a subscription can be offered here."
						: "Search the synced catalog."
			}
		>
			{selected ? (
				<div className="space-y-3">
					<button
						type="button"
						onClick={() => setSelected(null)}
						className="flex items-center gap-1 text-xs text-ink-muted transition-colors hover:text-ink"
					>
						<ChevronLeft className="h-3.5 w-3.5" strokeWidth={2} />
						All products
					</button>

					<button
						type="button"
						disabled={busy}
						onClick={() => pickVariant(null, true)}
						className="w-full rounded-md border border-line px-3 py-2.5 text-left transition-colors hover:border-line-strong disabled:opacity-50"
					>
						<span className="block text-sm font-medium text-ink">Let the customer choose</span>
						<span className="block text-xs text-ink-muted">
							Shopper picks the size at checkout. Falls back only when every variant is out of
							stock.
						</span>
					</button>

					{detail.isPending ? (
						<Skeleton className="h-32 w-full" />
					) : (
						<div className="max-h-64 space-y-1 overflow-y-auto">
							{detail.data?.variants.map((variant) => (
								<button
									key={variant.id}
									type="button"
									disabled={busy}
									onClick={() => pickVariant(variant.id, false)}
									className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-raised disabled:opacity-50"
								>
									<span className="min-w-0">
										<span className="block truncate text-sm text-ink">
											{variant.title ?? "Default"}
										</span>
										<span className="block truncate text-xs text-ink-faint">
											{variant.sku ?? "No SKU"}
										</span>
									</span>
									<span className="shrink-0 text-xs text-ink-muted">
										{formatMoney(variant.price, null)}
									</span>
								</button>
							))}
						</div>
					)}
				</div>
			) : (
				<div className="space-y-3">
					<div className="flex gap-2">
						<div className="relative flex-1">
							<Search
								className="pointer-events-none absolute top-2.5 left-2.5 h-4 w-4 text-ink-faint"
								strokeWidth={1.75}
							/>
							<Input
								autoFocus
								value={searchInput}
								placeholder="Search products"
								aria-label="Search products"
								onChange={(event) => setSearchInput(event.target.value)}
								className="pl-8"
							/>
						</div>
						<Select
							value={status}
							aria-label="Filter by status"
							onChange={(event) => setStatus(event.target.value)}
							className="w-36"
						>
							<option value="">All</option>
							<option value="ACTIVE">Active</option>
							<option value="DRAFT">Draft</option>
							<option value="ARCHIVED">Archived</option>
						</Select>
					</div>

					{list.isPending ? (
						<Skeleton className="h-56 w-full" />
					) : list.data && list.data.items.length === 0 ? (
						subscribableOnly && list.data.anySellingPlan === false ? (
							<EmptyState
								title="No products are sold on a subscription"
								description="Add a selling plan to a product in Shopify, then run a subscription sync from Settings. Until then there is nothing this offer can be pitched on."
							/>
						) : subscribableOnly ? (
							<EmptyState
								title="No subscribable products match"
								description="Only products with a selling plan can be offered here."
							/>
						) : (
							<EmptyState title="No products match" description="Try a different search." />
						)
					) : (
						<div className="max-h-72 space-y-0.5 overflow-y-auto">
							{list.data?.items.map((product) => (
								<ProductRow
									key={product.id}
									product={product}
									onSelect={() => setSelected(product)}
								/>
							))}
						</div>
					)}
				</div>
			)}
		</Dialog>
	);
};

export const PickerTrigger = ({ onClick, label }: { onClick: () => void; label: string }) => (
	<Button variant="outline" size="sm" onClick={onClick}>
		{label}
	</Button>
);
